<template>
  <li
    :class="{
      'color-row': true,
      'color-row--large': size === 'large',
    }"
  >
    <div
      :class="{
        'color-row__square': true,
        'color-row__square--large': size === 'large',
        'color-row__square--outlined': isOutlined,
      }"
      :style="swatch"
    ></div>
    <div
      :class="{
        'name-wrapper': true,
        'color-row__name-wrapper': true,
        'color-row__name-wrapper--large': size === 'large',
      }"
    >
      <div v-if="isEditable" class="edit-overlay name-wrapper__edit-overlay" @click="$emit('edit', $event)">
        <span class="edit-overlay__icon" v-html="icons.editIcon" />Edit
      </div>
      <div
        :class="{
          'name-wrapper__name': true,
          'name-wrapper__name--large': size === 'large',
          'name-wrapper__name--empty': !name,
        }"
      >
        {{ name || '(No name)' }}
      </div>
      <div v-if="code || color.paletteIds?.length" class="name-wrapper__bottom-line">
        <div
          v-if="code"
          :class="{
            'name-wrapper__code': true,
            'name-wrapper__code--large': size === 'large',
          }"
        >
          {{ code }}
        </div>
        <color-palette-snippet v-for="paletteId in color.paletteIds ?? []" :key="paletteId" :palette-id="paletteId" />
      </div>
    </div>
    <slot></slot>
  </li>
</template>

<script>
import colorPaletteIcons from '@lib/icons-imports/color-palettes';
import { calculateColorBrightness, convertRGBtoHEX } from '@lib/colorManagement';
import { swatchStyle } from '@lib/color-swatch';

import ColorPaletteSnippet from '@components/color-palette-snippet.vue';

import editIcon from '@icons/other/edit-filled.svg';

export default {
  name: 'color-row',
  props: {
    color: { type: Object, required: true }, //ColorDto
    isEditable: { type: Boolean, default: false },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['medium', 'large'].includes(value),
    },
  },

  components: { ColorPaletteSnippet },

  data() {
    return {
      icons: {
        colorPaletteIcons,
        editIcon,
      },
    };
  },

  computed: {
    // A colour drawn with a picture — a print — shows it; any other its flat colour.
    swatch: function () {
      return this.color.swatchUrl ? swatchStyle(this.color) : { backgroundColor: convertRGBtoHEX(this.color) };
    },
    name: function () {
      return this.color.name;
    },
    code: function () {
      return this.color.code;
    },
    isOutlined: function () {
      return !this.color.swatchUrl && calculateColorBrightness(this.color) > 240;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables';

.edit-overlay {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgb(255, 255, 255);
  font-size: 1.3rem;
  line-height: 1.6rem;
  color: $c-dark;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
  transition: opacity 0.12s ease;

  &:hover {
    opacity: 1;
  }

  &__icon {
    width: 1rem;
    height: 1rem;
    margin-right: 0.8rem;
    fill: $c-dark;
    display: block;
  }
}

.name-wrapper {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &__name {
    margin-bottom: 0.3rem;

    font-size: 1.2rem;
    line-height: 1.4rem;
    font-weight: 400;
    color: $c-dark;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &--large {
      font-size: 1.4rem;
      line-height: 1.7rem;
    }

    &--empty {
      font-style: italic;
    }
  }

  &__bottom-line {
    height: 1.2rem;
    display: flex;
    align-items: center;
  }

  &__code {
    margin-right: 0.5rem;

    font-size: 1rem;
    line-height: 1.2rem;
    font-weight: 400;
    color: $c-dark;
    opacity: 0.5;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &--large {
      font-size: 1.2rem;
      line-height: 1.4rem;
    }
  }
}

.color-row {
  padding: 0.6rem 0.8rem;
  border-radius: 0.5rem;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  &--large {
    padding: 0.8rem 1rem;
  }

  &__square {
    flex: 0 0 auto;
    width: 3rem;
    height: 3rem;

    border-radius: 0.5rem;

    &--large {
      width: 3.4rem;
      height: 3.4rem;
    }

    &--outlined {
      border: 1px solid $c-dark;
    }
  }

  &__name-wrapper {
    flex: 1;
    height: 3rem;
    position: relative;

    &--large {
      height: 3.4rem;
    }
  }
}
</style>
